import { useEffect, useMemo, useRef, useState } from 'react';
import { PaneSwitch, type Pane } from '@/components/pane-switch';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ClientQuote,
  type CombinedQuoteResponse,
  type PatchCustomerQuoteResponse,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  rfqId: string;
  clientQuotes: ClientQuote[];
  partNumberByLineId: Record<string, string>;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};
function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function CombinedQuoteModal({ open, onClose, rfqId, clientQuotes, partNumberByLineId }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const dismiss = useModalDismiss(onClose);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<CombinedQuoteResponse | null>(null);
  // 'bundle' = one quote per part, grand total is a sum (default).
  // 'alternatives' = multiple quotes per part allowed; renders as customer-
  // selectable options with a $min–$max range total.
  const [mode, setMode] = useState<'bundle' | 'alternatives'>('bundle');

  // Sort by line number for a stable, readable list.
  const quotes = useMemo(
    () => [...clientQuotes].sort((a, b) => a.lineNumber - b.lineNumber),
    [clientQuotes],
  );

  // Pick ONE quote per line item, matching the given currency. A line can have
  // multiple client_quotes (sent + revised draft); the combined doc must carry
  // at most one per part or the grand total double-counts.
  function onePerLine(baseCurrency: string | undefined): Set<string> {
    const seenLines = new Set<string>();
    const pick = new Set<string>();
    for (const q of quotes) {
      if (q.currency !== baseCurrency) continue;
      if (seenLines.has(q.rfqLineItemId)) continue;
      seenLines.add(q.rfqLineItemId);
      pick.add(q.id);
    }
    return pick;
  }

  // On open, pre-select one quote per line in the first quote's currency
  // (the common single-currency RFQ case). Reset the rendered result.
  useEffect(() => {
    if (!open) return;
    setSelected(onePerLine(quotes[0]?.currency));
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quotes]);

  // Currency lock: once something is selected, only same-currency quotes can join.
  const selectedCurrency = useMemo(() => {
    for (const q of quotes) if (selected.has(q.id)) return q.currency;
    return null;
  }, [quotes, selected]);

  const selectedTotal = useMemo(
    () => quotes.filter((q) => selected.has(q.id)).reduce((s, q) => s + q.customerTotalCents, 0),
    [quotes, selected],
  );
  const selectedDraftIds = useMemo(
    () => quotes.filter((q) => selected.has(q.id) && q.status === 'draft').map((q) => q.id),
    [quotes, selected],
  );

  // Alternatives-mode running total: customer picks ONE option per line, so the
  // floor is sum-of-cheapest-per-line and the ceiling is sum-of-most-expensive.
  // Same calc the server does — kept in sync visually so what you see in the
  // modal matches the doc that gets rendered. null when not in alternatives mode.
  const selectedRange = useMemo(() => {
    if (mode !== 'alternatives') return null;
    const byLine = new Map<string, number[]>();
    for (const q of quotes) {
      if (!selected.has(q.id)) continue;
      const arr = byLine.get(q.rfqLineItemId) ?? [];
      arr.push(q.customerTotalCents);
      byLine.set(q.rfqLineItemId, arr);
    }
    let minCents = 0;
    let maxCents = 0;
    for (const totals of byLine.values()) {
      minCents += Math.min(...totals);
      maxCents += Math.max(...totals);
    }
    return { minCents, maxCents, hasSpread: minCents !== maxCents };
  }, [mode, quotes, selected]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      // Bundle mode: enforce one quote per line — selecting a new quote drops
      // any other already-selected quote for the same part (would otherwise
      // double-count the grand total).
      // Alternatives mode: allow multi-select per line (rendered as Options).
      if (mode === 'bundle') {
        const q = quotes.find((x) => x.id === id);
        if (q) {
          for (const other of quotes) {
            if (other.id !== id && other.rfqLineItemId === q.rfqLineItemId) next.delete(other.id);
          }
        }
      }
      next.add(id);
      return next;
    });
    setResult(null);
  }

  // "Select all": bundle mode picks one quote per line in the locked currency;
  // alternatives mode picks EVERY quote in the locked currency (multi-option).
  function selectAll(): Set<string> {
    const baseCurrency = selectedCurrency ?? quotes[0]?.currency;
    if (!baseCurrency) return new Set();
    if (mode === 'alternatives') {
      return new Set(quotes.filter((q) => q.currency === baseCurrency).map((q) => q.id));
    }
    return onePerLine(baseCurrency);
  }

  // Mode change does NOT reset the selection — a set of client_quote IDs is
  // valid in either mode. Bundle just hides the multi-per-line option set; if
  // the user already picked one-per-line they can keep it, and if they later
  // want alternatives they can pick additional ones for the same line without
  // losing their current choices.

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['client-quotes'] });
    qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  const generate = useMutation({
    mutationFn: async () => {
      return api<CombinedQuoteResponse>('/api/customer-quotes/combined', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ clientQuotePublicIds: [...selected], mode }),
      });
    },
    onSuccess: (data) => setResult(data),
  });

  // Send the combined quote AS the email body via GHL, marking every included
  // quote sent in one call. Alternatives mode gives each option its own accept
  // link; bundle mode is reply-only (acceptance is per-line — no single row
  // means "accept the bundle").
  const [emailed, setEmailed] = useState<{ to: string; subject: string; acceptLinks: number } | null>(null);
  const sendKeyRef = useRef<string>(crypto.randomUUID());
  const emailCombined = useMutation({
    mutationFn: async () => {
      return api<{ status: 'sent'; to: string; subject: string; acceptLinks: number; quoteRefs: string[] }>(
        '/api/customer-quotes/combined/send-email',
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          idempotencyKey: sendKeyRef.current,
          body: JSON.stringify({ clientQuotePublicIds: [...selected], mode }),
        },
      );
    },
    onSuccess: (data) => {
      setEmailed({ to: data.to, subject: data.subject, acceptLinks: data.acceptLinks });
      invalidate();
    },
  });

  const markSent = useMutation({
    mutationFn: async () => {
      // Only drafts transition; sent/accepted are left as-is.
      await Promise.all(
        selectedDraftIds.map((id) =>
          api<PatchCustomerQuoteResponse>(`/api/customer-quotes/${id}`, {
            method: 'PATCH',
            tokenGetter: () => getToken(),
            body: JSON.stringify({ status: 'sent' }),
          }),
        ),
      );
    },
    onSuccess: () => invalidate(),
  });

  useEffect(() => {
    if (!result || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(result.html);
    doc.close();
  }, [result]);

  const downloadHtml = () => {
    if (!result) return;
    const blob = new Blob([result.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.rfqRef}-combined-quote.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const copyHtml = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result.html); }
    catch { window.prompt('Copy the HTML manually:', result.html.slice(0, 100) + '…'); }
  };
  const openInNewTab = () => {
    if (!result) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open(); win.document.write(result.html); win.document.close();
  };

  // Phones show one pane at a time (see pane-switch.tsx). The preview takes over as soon as a
  // document exists or is regenerated, and every open starts on the right pane.
  const [pane, setPane] = useState<Pane>('edit');
  const previewReady = !!result;
  useEffect(() => {
    setPane(previewReady ? 'preview' : 'edit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" {...dismiss}>
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Combine quotes into one document</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pick the line quotes to include, generate one branded quote with a grand total, then send it.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <PaneSwitch pane={pane} onChange={setPane} previewReady={previewReady} editLabel={'Edit'} />
        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr]">
          <aside className={`border-b lg:border-r border-border lg:overflow-auto ${pane === 'edit' ? '' : 'hidden lg:block'} p-4 space-y-3 bg-muted/20`}>
            {quotes.length === 0 ? (
              <div className="text-sm text-muted-foreground">No line quotes yet. Generate per-line quotes first, then combine them here.</div>
            ) : (
              <>
                {/* Mode toggle — "alternatives" lets you put multiple quotes
                    for the SAME part on the customer doc as labelled options. */}
                <div className="flex items-center justify-between gap-2 pb-3 border-b border-border">
                  <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={mode === 'alternatives'}
                      onChange={(e) => setMode(e.target.checked ? 'alternatives' : 'bundle')}
                    />
                    <span className="font-medium">Show as alternatives</span>
                    <span className="text-muted-foreground">— customer picks one per part</span>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Line quotes ({quotes.length})</div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(selectAll());
                      setResult(null);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Select all{selectedCurrency ? ` (${selectedCurrency})` : ''}
                  </button>
                </div>

                <div className="space-y-1.5">
                  {quotes.map((q) => {
                    const mismatch = selectedCurrency != null && q.currency !== selectedCurrency && !selected.has(q.id);
                    const pn = partNumberByLineId[q.rfqLineItemId] ?? `Line ${q.lineNumber}`;
                    return (
                      <label
                        key={q.id}
                        className={`flex items-start gap-2 p-2 rounded border text-sm cursor-pointer ${selected.has(q.id) ? 'border-primary bg-primary/5' : 'border-border bg-background'} ${mismatch ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={selected.has(q.id)}
                          disabled={mismatch}
                          onChange={() => toggle(q.id)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-mono text-xs font-semibold">L{q.lineNumber} · {pn}</span>
                          <span className="ml-1 px-1 py-0.5 text-[10px] uppercase tracking-wide rounded bg-muted text-muted-foreground">{q.status}</span>
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            {q.qty} ea · {fmtMoney(q.customerTotalCents, q.currency)}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>

                <div className="border-t border-border pt-3 text-sm">
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">{selected.size} selected</span>
                    {selectedCurrency ? (
                      selectedRange && selectedRange.hasSpread ? (
                        <span className="font-mono font-semibold text-primary text-xs">
                          {fmtMoney(selectedRange.minCents, selectedCurrency)} — {fmtMoney(selectedRange.maxCents, selectedCurrency)}
                        </span>
                      ) : (
                        <span className="font-mono font-semibold text-primary">{fmtMoney(selectedTotal, selectedCurrency)}</span>
                      )
                    ) : (
                      <span className="font-mono font-semibold text-primary">—</span>
                    )}
                  </div>
                  {selectedRange?.hasSpread && (
                    <div className="text-[10px] text-muted-foreground mt-1 italic">Range — customer picks one option per part</div>
                  )}
                </div>

                <button
                  onClick={() => generate.mutate()}
                  disabled={selected.size === 0 || generate.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {generate.isPending ? 'Generating…' : `Generate combined quote (${selected.size})`}
                </button>

                {result && (
                  <div className="space-y-2 border-t border-border pt-3">
                    <div className="text-xs text-muted-foreground">
                      <span className="font-mono">{result.combinedRef}</span> · {fmtMoney(result.grandTotalCents, result.currency)} · valid until {new Date(result.validUntil).toLocaleDateString()}
                    </div>
                    {emailed ? (
                      <div className="border border-emerald-600/40 bg-emerald-600/10 rounded p-3 space-y-1">
                        <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Emailed to {emailed.to}</div>
                        <div className="text-[11px] text-muted-foreground break-words">{emailed.subject}</div>
                        {emailed.acceptLinks > 0 && (
                          <div className="text-[11px] text-muted-foreground">{emailed.acceptLinks} one-click accept links included.</div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => emailCombined.mutate()}
                        disabled={emailCombined.isPending}
                        className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                      >
                        {emailCombined.isPending
                          ? 'Sending…'
                          : mode === 'alternatives'
                          ? 'Email options to customer'
                          : 'Email combined quote to customer'}
                      </button>
                    )}
                    {emailCombined.isError && (
                      <div className="text-xs text-destructive">{(emailCombined.error as { message?: string })?.message ?? 'Send failed'}</div>
                    )}
                    <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download .html</button>
                    <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy HTML to clipboard</button>
                    <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                    {selectedDraftIds.length > 0 && (
                      <button
                        onClick={() => markSent.mutate()}
                        disabled={markSent.isPending}
                        className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                      >
                        {markSent.isPending ? 'Updating…' : `Mark ${selectedDraftIds.length} draft${selectedDraftIds.length === 1 ? '' : 's'} as sent`}
                      </button>
                    )}
                    {markSent.isSuccess && <div className="text-xs text-emerald-700">Marked as sent.</div>}
                  </div>
                )}

                {generate.isError && <div className="text-sm text-destructive">{(generate.error as { message?: string })?.message ?? 'Generate failed'}</div>}
                {markSent.isError && <div className="text-sm text-destructive">{(markSent.error as { message?: string })?.message ?? 'Update failed'}</div>}
              </>
            )}
          </aside>

          <section className={`overflow-hidden bg-[#f4f6fa] items-stretch min-h-[55dvh] lg:min-h-0 ${pane === 'preview' ? 'flex' : 'hidden lg:flex'}`}>
            {result ? (
              <iframe ref={iframeRef} title="Combined quote" className="w-full h-full border-0 bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground px-6 text-center">
                Select the line quotes on the left and click <span className="mx-1 font-medium">Generate combined quote</span>.
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">Done</button>
        </div>
      </div>
    </div>
  );
}
